import React, { useEffect, useRef, useState } from "react";
import {
  Anchor,
  Box,
  Button,
  CopyButton,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconArrowLeft, IconCheck, IconCopy, IconPencil, IconRocket } from "@tabler/icons-react";
import { TxLookupResult, TxOutput, parseTxInput } from "../helpers/txLookup";
import {
  FeeBumpData,
  FeeUnit,
  Wallet,
  WalletPath,
  feeBumpData,
  fillStep,
  findWallet,
} from "../helpers/feeBumpWallets";
import { formatBtc, formatSats } from "../../checkBitcoinAddress/helpers/addressLookup";
import { trackGoal } from "../../helpers/trackGoal";
import WalletPicker, { Choice, Labelled, WizardStyles } from "./WalletPicker";

/**
 * "Speed it up": one question at a time, for someone who has never heard of RBF.
 *   role → (receiving or exchange: which output is yours) → wallet → steps → (after RBF) new txid
 * A sender whose way is CPFP is asked after the wallet which output is their change.
 * The answers live in the URL (?txid=…&role=sent&wallet=trezor-suite, plus &output=1 for the
 * receiver's output or the sender's change), so a link or a recording opens any step; each step
 * has a stable data-wizard-step.
 * The numbers come from the live advice (TxLookup.advice), in the unit each wallet asks for.
 */

export type Role = "sent" | "receiving" | "exchange";

export interface WizardState {
  role: Role | null;
  /** vout index (the receiver's output, or the sender's change), or "none" when none of the outputs is theirs */
  output: number | "none" | null;
  wallet: string | null;
  /** the hardware wallet, when its own app can't and the steps are Sparrow's */
  via: string | null;
  step: "new-txid" | null;
}

export const EMPTY_WIZARD: WizardState = { role: null, output: null, wallet: null, via: null, step: null };

const ROLES: Role[] = ["sent", "receiving", "exchange"];
const ID_PARAM = /^[a-z0-9-]{1,40}$/;
// the smallest output a child can leave behind (P2TR's dust limit; P2WPKH's is 294)
const DUST_SATS = 330;

export function readWizardState(search: string): WizardState {
  const params = new URLSearchParams(search);
  const role = params.get("role") as Role;
  if (!ROLES.includes(role)) return EMPTY_WIZARD;
  const rawOutput = params.get("output");
  const output = rawOutput === "none" ? "none" : rawOutput && /^\d{1,4}$/.test(rawOutput) ? Number(rawOutput) : null;
  const id = (name: string) => {
    const value = params.get(name);
    return value && ID_PARAM.test(value) ? value : null;
  };
  return {
    role,
    output,
    wallet: id("wallet"),
    via: id("via"),
    step: params.get("step") === "new-txid" ? "new-txid" : null,
  };
}

/** "&role=sent&wallet=trezor-suite", appended to the share URL */
export function wizardParams(state: WizardState): string {
  const pairs: [string, string | number | null][] = [
    ["role", state.role],
    ["output", state.output],
    ["wallet", state.wallet],
    ["via", state.via],
    ["step", state.step],
  ];
  return pairs
    .filter(([, value]) => value !== null)
    .map(([key, value]) => `&${key}=${value}`)
    .join("");
}

const PANEL_STYLE = {
  backgroundColor: "rgba(0, 0, 0, 0.35)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
};
const MONO = { fontFamily: "monospace", fontVariantNumeric: "tabular-nums" as const };

const ROLE_ANSWER: Record<Role, string> = {
  sent: "I sent it",
  receiving: "I'm receiving it",
  exchange: "Exchange withdrawal",
};

const RBF_INTRO =
  "Your wallet sends the same payment again with a higher fee; this is called replace-by-fee, or RBF.";
const CPFP_RECEIVE_INTRO =
  "You send the bitcoin you're receiving on to yourself, with a fee big enough to pay for both transactions. Miners can only take the new one together with the stuck one, so they take both. This is called child pays for parent, or CPFP.";
const CPFP_SEND_INTRO =
  "Most payments send some bitcoin back to your own wallet as change. You send that change on to yourself, with a fee big enough to pay for both transactions, and miners take the two together. This is called child pays for parent, or CPFP.";
const WAIT_TEXT =
  "Or wait. It confirms when fees drop. If it never does, nodes drop it after about two weeks and the coins stay with the sender.";

type Advice = NonNullable<TxLookupResult["advice"]>;

const ceilTo = (value: number, digits: number) => {
  const factor = 10 ** digits;
  return Math.ceil(value * factor - 1e-9) / factor;
};

interface NumberToEnter {
  label: string;
  value: string; // what the copy button copies
  unit: string;
}

/** The number(s) the wallet's fee field asks for. */
function numbersFor(enter: WalletPath["enter"], method: "rbf" | "cpfp", advice: Advice, whole: boolean): NumberToEnter[] {
  if (Array.isArray(enter)) return enter.flatMap((unit) => numberFor(unit, method, advice, whole));
  return numberFor(enter, method, advice, whole);
}

function numberFor(unit: FeeUnit | undefined, method: "rbf" | "cpfp", advice: Advice, whole: boolean): NumberToEnter[] {
  const rate = (value: number) => String(whole ? Math.ceil(value - 1e-9) : ceilTo(value, 1));
  const sats = (value: number) => String(Math.ceil(value));
  switch (unit) {
    case "fee_rate":
      return [{ label: "New fee rate", value: rate(advice.rbf_fee_rate), unit: "sat/vB" }];
    case "total_fee":
      return [{ label: "New total fee", value: sats(advice.rbf_fee_sats), unit: "sats" }];
    case "child_fee_rate":
      return [{ label: "Fee rate for the new transaction", value: rate(advice.cpfp_child_fee_rate), unit: "sat/vB" }];
    case "effective_rate":
      return [{ label: "Fee rate for both transactions together", value: rate(advice.target_fee_rate), unit: "sat/vB" }];
    case "child_fee":
      return [{ label: "Fee for the new transaction", value: sats(advice.cpfp_child_fee_sats), unit: "sats" }];
    case "preset":
      return [
        {
          label: "Choose the fastest option. It should be at least",
          value: rate(method === "rbf" ? advice.rbf_fee_rate : advice.cpfp_child_fee_rate),
          unit: "sat/vB",
        },
      ];
    case "none":
      return [];
    default:
      // "Something else": we don't know what it asks for, so give both
      return method === "rbf"
        ? [
            { label: "If it asks for a fee rate", value: rate(advice.rbf_fee_rate), unit: "sat/vB" },
            { label: "If it asks for the total fee", value: sats(advice.rbf_fee_sats), unit: "sats" },
          ]
        : [
            { label: "If it asks for a fee rate", value: rate(advice.cpfp_child_fee_rate), unit: "sat/vB" },
            { label: "If it asks for the fee in sats", value: sats(advice.cpfp_child_fee_sats), unit: "sats" },
          ];
  }
}

interface Route {
  method: "rbf" | "cpfp";
  /** the wallet whose steps these are (Sparrow, when a hardware wallet's app can't) */
  wallet: Wallet;
  /** the hardware wallet being connected to Sparrow */
  device: Wallet | null;
  path: WalletPath;
  notice: string | null;
}

const deviceName = (wallet: Wallet) => wallet.device || wallet.name;

/** The wallet replaces this payment itself: RBF, with the signal if it needs one. */
const bumpsIt = (wallet: Wallet, tx: TxLookupResult) =>
  wallet.send.method === "rbf" && !(wallet.send.signal_required && !tx.rbf_signaled);

/**
 * How a sender speeds it up in this wallet: RBF, else CPFP on their change (the wizard then asks
 * which output that is), else Sparrow; null if nothing works.
 */
function senderRoute(data: FeeBumpData, wallet: Wallet, tx: TxLookupResult, device: Wallet | null = null): Route | null {
  const send = wallet.send;
  if (bumpsIt(wallet, tx)) return { method: "rbf", wallet, device, path: send, notice: null };
  if (send.method === "cpfp") return { method: "cpfp", wallet, device, path: send, notice: null };

  const why = send.method === "rbf"
    ? `This transaction doesn't signal RBF, so ${wallet.name} won't offer to bump its fee.`
    : `${wallet.name} can't bump the fee of a payment you sent.`;
  if (wallet.send_cpfp?.method === "cpfp") {
    return { method: "cpfp", wallet, device, path: wallet.send_cpfp, notice: `${why} You can speed it up through your change instead.` };
  }
  if (wallet.fallback === "sparrow" && !device) {
    const sparrow = findWallet(data, "sparrow");
    const route = sparrow && senderRoute(data, sparrow, tx, wallet);
    if (route) return { ...route, notice: `${why} Connect your ${deviceName(wallet)} to Sparrow (free) and do it there.` };
  }
  return null;
}

function receiverRoute(data: FeeBumpData, wallet: Wallet, device: Wallet | null = null): Route | null {
  if (wallet.receive.method === "cpfp") return { method: "cpfp", wallet, device, path: wallet.receive, notice: null };
  if (wallet.fallback === "sparrow" && !device) {
    const sparrow = findWallet(data, "sparrow");
    const route = sparrow && receiverRoute(data, sparrow, wallet);
    if (route) {
      return {
        ...route,
        notice: `${wallet.name} can't speed up a payment you're receiving. Connect your ${deviceName(wallet)} to Sparrow (free) and do it there.`,
      };
    }
  }
  return null;
}

/** The Copy button used for the txid, the share link and the numbers. */
const CopyIt: React.FC<{ value: string; label?: string; size?: "xs" | "sm" }> = ({ value, label = "Copy", size = "xs" }) => (
  <CopyButton value={value}>
    {({ copied, copy }) => (
      <Button
        variant={copied ? "filled" : "outline"}
        color={copied ? "teal" : "gray"}
        size={size}
        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
        onClick={copy}
      >
        {copied ? "Copied" : label}
      </Button>
    )}
  </CopyButton>
);

const Question: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text c="white" fw={700} size="xl" lh={1.3}>
    {children}
  </Text>
);

const NumberBox: React.FC<{ number: NumberToEnter }> = ({ number }) => (
  <Paper p="sm" radius="md" style={{ backgroundColor: "rgba(251, 146, 60, 0.08)", border: "1px solid rgba(251, 146, 60, 0.45)" }}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
      {number.label}
    </Text>
    <Group justify="space-between" align="center" gap="xs" mt={2}>
      <Text c="white" fw={700} style={{ ...MONO, fontSize: 30, lineHeight: 1.15 }}>
        {Number(number.value).toLocaleString("en-US", { maximumFractionDigits: 1 })}{" "}
        <Text span c="dimmed" fw={600} style={{ fontSize: 18 }}>
          {number.unit}
        </Text>
      </Text>
      <CopyIt value={number.value} label="Copy number" size="sm" />
    </Group>
  </Paper>
);

const SpeedUpWizard: React.FC<{
  tx: TxLookupResult;
  state: WizardState;
  onChange: (state: WizardState) => void;
  onCheckTxid: (txid: string) => void;
  shareUrl: string;
}> = ({ tx, state, onChange, onCheckTxid, shareUrl }) => {
  const data = feeBumpData();
  const rootRef = useRef<HTMLDivElement>(null);
  const [unsure, setUnsure] = useState(false);
  const [newTxid, setNewTxid] = useState("");
  const [newTxidError, setNewTxidError] = useState<string | null>(null);
  const advice = tx.advice;
  // " ($0.17)": cents are enough to read out
  const usd = (sats: number) => {
    if (!tx.btc_usd_price) return "";
    const dollars = (sats / 100_000_000) * tx.btc_usd_price;
    if (dollars < 0.01) return " (less than a cent)";
    return ` (${dollars.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })})`;
  };

  const role = state.role;
  const receiver = role === "receiving" || role === "exchange";
  const outputs = tx.outputs.map((output, index) => ({ output, index })).filter(({ output }) => !!output.address);
  const chosen: TxOutput | null =
    typeof state.output === "number" && tx.outputs[state.output]?.address ? tx.outputs[state.output] : null;
  const tooSmall = !!chosen && !!advice && chosen.sats < advice.cpfp_child_fee_sats + DUST_SATS;
  // none of the outputs is theirs, or theirs can't pay for a child: no CPFP for them
  const nothingToSpend = state.output === "none" || tooSmall;
  const wallet = data ? findWallet(data, state.wallet) : null;
  // only "Sparrow, by way of a hardware wallet" (Show me how with Sparrow) uses it
  const viaWallet = data && state.wallet === "sparrow" ? findWallet(data, state.via) : null;
  const via = viaWallet?.fallback === "sparrow" ? viaWallet : null;

  // which step is showing
  let view: "role" | "output" | "wallet" | "steps" | "new-txid";
  if (!role) view = "role";
  else if (receiver && state.output !== "none" && !chosen) view = "output";
  else if (receiver && nothingToSpend) view = "steps";
  else if (!wallet) view = "wallet";
  else view = "steps";

  let route: Route | null = null;
  if (view === "steps" && wallet && data && wallet.kind !== "custodial" && !(receiver && nothingToSpend)) {
    if (via) {
      route = role === "sent" ? senderRoute(data, wallet, tx, via) : receiverRoute(data, wallet, via);
      if (route) route = { ...route, notice: null };
    } else {
      route = role === "sent" ? senderRoute(data, wallet, tx) : receiverRoute(data, wallet);
    }
  }
  // a sender's CPFP spends their change: ask which output that is
  const throughChange = role === "sent" && route?.method === "cpfp";
  if (throughChange && state.output !== "none" && !chosen) view = "output";
  if (view === "steps" && state.step === "new-txid" && route?.method === "rbf") view = "new-txid";

  const stepsKey = view === "steps" ? `${role}:${state.wallet}:${state.via}:${state.output}` : null;
  useEffect(() => {
    if (stepsKey) trackGoal("Speed-up steps shown", { role: role ?? "", wallet: state.wallet ?? "none" });
  }, [stepsKey]);

  if (!data || !advice) return null;

  // A choice moves on to the next question; keep the question's buttons on screen.
  const go = (next: WizardState) => {
    setUnsure(false);
    onChange(next);
    window.requestAnimationFrame(() => {
      const element = rootRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.5) {
        window.scrollTo({ top: window.scrollY + rect.top - 12, behavior: "instant" as ScrollBehavior });
      }
    });
  };

  // a sender's change is asked after the wallet, so a new wallet asks it again
  const withWallet = (id: string | null, viaId: string | null = null): WizardState => ({
    ...state,
    wallet: id,
    via: viaId,
    step: null,
    output: role === "sent" ? null : state.output,
  });

  const back = () => {
    if (unsure) return setUnsure(false);
    if (view === "new-txid") return go({ ...state, step: null });
    if (throughChange && state.output !== null) return go({ ...state, output: null, step: null });
    if (state.via) return go(withWallet(state.via));
    if (state.wallet) return go(withWallet(null));
    if (state.output !== null) return go({ ...state, output: null });
    go(EMPTY_WIZARD);
  };

  const submitNewTxid = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseTxInput(newTxid);
    if (parsed.kind !== "txid") {
      setNewTxidError("That isn't a transaction ID. It's 64 characters of 0–9 and a–f.");
      return;
    }
    if (parsed.txid === tx.txid) {
      setNewTxidError("That's the old ID. After a fee bump your wallet shows a new one in the transaction's details.");
      return;
    }
    setNewTxidError(null);
    onCheckTxid(parsed.txid);
  };

  // ---- the answers so far, each one a button back to its question
  const answers: { label: string; to: WizardState }[] = [];
  if (role) answers.push({ label: ROLE_ANSWER[role], to: EMPTY_WIZARD });
  const outputLabel = chosen
    ? `${chosen.address.slice(0, 8)}…${chosen.address.slice(-6)}`
    : role === "sent"
      ? "No change"
      : "None of these";
  if (receiver && (chosen || state.output === "none")) {
    answers.push({ label: outputLabel, to: { ...state, output: null, wallet: null, via: null, step: null } });
  }
  if (wallet) {
    answers.push({ label: via ? `${via.name} → ${wallet.name}` : wallet.name, to: withWallet(null) });
  }
  if (throughChange && (chosen || state.output === "none")) {
    answers.push({ label: outputLabel, to: { ...state, output: null, step: null } });
  }

  const askSenderBlock = (lead: React.ReactNode) => (
    <Stack gap="sm">
      <Question>Ask the sender to speed it up</Question>
      <Text size="sm" c="dimmed">
        {lead}
      </Text>
      <Group gap="xs">
        <CopyIt value={shareUrl} label="Copy the link to this page" size="sm" />
      </Group>
      <Fallbacks title="Or" />
    </Stack>
  );

  const askExchangeBlock = (lead: React.ReactNode, exchange = "the sending exchange") => (
    <Stack gap="sm">
      <Question>Ask {exchange}'s support, with the transaction ID</Question>
      <Text size="sm" c="dimmed">
        {lead}
      </Text>
      <Paper p="sm" radius="md" style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          Transaction ID
        </Text>
        <Text size="sm" c="white" style={{ ...MONO, overflowWrap: "anywhere" }} mb={6}>
          {tx.txid}
        </Text>
        <CopyIt value={tx.txid} label="Copy transaction ID" size="sm" />
      </Paper>
      <Fallbacks title="Or" />
    </Stack>
  );

  // the accelerator and waiting, plus Sparrow for a hardware wallet that isn't there yet
  function Fallbacks({ sparrowFor, title = "If that doesn't work" }: { sparrowFor?: Wallet | null; title?: string }) {
    const accelerator = `${data.accelerator.tx_url}${tx.txid}`;
    return (
      <Stack gap={8}>
        <Divider color="rgba(255,255,255,0.08)" label={title} labelPosition="left" />
        {sparrowFor && (
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Text size="sm" c="dimmed" style={{ flex: 1 }}>
              Connect your {deviceName(sparrowFor)} to Sparrow (free) and do it there.
            </Text>
            <Button size="xs" variant="outline" color="orange" onClick={() => go(withWallet("sparrow", sparrowFor.id))}>
              Show me how with Sparrow
            </Button>
          </Group>
        )}
        <Text size="sm" c="dimmed">
          Pay to speed it up with{" "}
          <Anchor href={accelerator} target="_blank" rel="noopener nofollow" c="orange.4" size="sm">
            the mempool.space Accelerator
          </Anchor>
          . <Labelled text={data.accelerator.note} />
        </Text>
        <Text size="sm" c="dimmed">
          {WAIT_TEXT}
        </Text>
      </Stack>
    );
  }

  // ---- step bodies
  let body: React.ReactNode;
  if (view === "role" && unsure) {
    body = (
      <Stack gap="sm" data-wizard-step="role" data-wizard-view="not-sure">
        <Question>How to tell</Question>
        <Text size="sm" c="dimmed">
          Open your wallet and find this payment. In the sender's wallet it's listed as sent, usually with a minus sign. In the
          receiver's wallet it's listed as incoming, or pending.
        </Text>
        <Text size="sm" c="dimmed">
          If you're waiting for bitcoin you withdrew from an exchange or an app (Coinbase, Binance, Kraken, Cash App, Strike,
          River…), it's a withdrawal.
        </Text>
        <Group>
          <Button variant="outline" color="orange" onClick={() => setUnsure(false)}>
            Back to the question
          </Button>
        </Group>
      </Stack>
    );
  } else if (view === "role") {
    body = (
      <Stack gap="sm" data-wizard-step="role">
        <Question>Did you send this payment, or are you receiving it?</Question>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" style={{ alignItems: "start" }}>
          <Choice onClick={() => go({ ...EMPTY_WIZARD, role: "sent" })}>I sent it</Choice>
          <Choice onClick={() => go({ ...EMPTY_WIZARD, role: "receiving" })}>I'm receiving it</Choice>
          <Box>
            <Choice onClick={() => go({ ...EMPTY_WIZARD, role: "exchange" })}>It's a withdrawal from an exchange or app</Choice>
            <Text size="xs" c="dimmed" mt={4}>
              Coinbase, Binance, Kraken, Cash App, Strike, River…
            </Text>
          </Box>
          <Choice onClick={() => setUnsure(true)}>I'm not sure</Choice>
        </SimpleGrid>
      </Stack>
    );
  } else if (view === "output") {
    body = (
      <OutputStep
        outputs={outputs}
        extra={tx.output_count - tx.outputs.length}
        kind={role === "sent" ? "change" : role === "exchange" ? "exchange" : "receiving"}
        notice={route?.notice ?? null}
        onPick={(index) =>
          go(role === "sent" ? { ...state, output: index, step: null } : { ...state, output: index, wallet: null, via: null, step: null })
        }
      />
    );
  } else if (view === "wallet") {
    body = (
      <Stack gap="sm" data-wizard-step="wallet">
        <Question>{role === "exchange" ? "Which wallet did you withdraw to?" : "Which wallet are you using?"}</Question>
        <WalletPicker data={data} selectedId={state.wallet} onPick={(picked) => go(withWallet(picked.id))} />
      </Stack>
    );
  } else if (view === "new-txid") {
    body = (
      <Stack gap="sm" data-wizard-step="new-txid">
        <Question>Paste the new transaction ID</Question>
        <Text size="sm" c="dimmed">
          The fee bump made a new transaction with a new ID, and the old one disappears: this page will say "not found" for it.
        </Text>
        {wallet && (
          <Text size="sm" c="dimmed">
            <Labelled text={wallet.txid} />
          </Text>
        )}
        <form onSubmit={submitNewTxid}>
          <Stack gap="xs">
            <TextInput
              aria-label="New transaction ID"
              placeholder="New transaction ID"
              value={newTxid}
              onChange={(e) => {
                setNewTxid(e.currentTarget.value);
                setNewTxidError(null);
              }}
              error={newTxidError}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              size="md"
              styles={{ input: { fontFamily: "monospace", backgroundColor: "rgba(0, 0, 0, 0.3)", border: "1px solid rgba(255, 255, 255, 0.15)", color: "white" } }}
            />
            <Group>
              <Button type="submit" color="orange" disabled={!newTxid.trim()}>
                Check the new ID
              </Button>
            </Group>
          </Stack>
        </form>
        <Text size="xs" c="dimmed">
          The page then checks the new transaction again on every new block, until it confirms.
        </Text>
      </Stack>
    );
  } else if (receiver && state.output === "none") {
    body = (
      <Box data-wizard-step="steps" data-wizard-path={role === "exchange" ? "ask-exchange" : "ask-sender"}>
        {role === "exchange"
          ? askExchangeBlock(
              "If it went to another exchange or app, neither you nor the receiving exchange can change its fee. Only the exchange that sent it can, so contact its support and give them the transaction ID."
            )
          : askSenderBlock(
              "If none of these addresses is yours, you can't speed up this payment yourself. The sender can: send them the link to this page."
            )}
      </Box>
    );
  } else if (receiver && tooSmall && chosen) {
    const lead = `Your output holds ${formatSats(chosen.sats)}. Speeding it up from there costs about ${formatSats(
      advice.cpfp_child_fee_sats
    )} in fees, ${chosen.sats <= advice.cpfp_child_fee_sats ? "more than it holds" : "which leaves too little to send on"}.`;
    body = (
      <Box data-wizard-step="steps" data-wizard-path="too-small">
        {role === "exchange"
          ? askExchangeBlock(`${lead} Only the exchange that sent it can speed it up now.`)
          : askSenderBlock(`${lead} The sender can speed it up instead: send them the link to this page.`)}
      </Box>
    );
  } else if (throughChange && nothingToSpend && route && wallet) {
    // the sender's wallet can't replace it, and there's no change (big enough) to spend
    const noBump = tx.rbf_signaled
      ? `${wallet.name} can't bump the fee of a payment you sent.`
      : `This transaction doesn't signal RBF, so ${route.wallet.id === wallet.id ? wallet.name : `${wallet.name} and Sparrow`} won't offer to bump its fee.`;
    const noChange =
      tooSmall && chosen
        ? `The other way is to spend your change, which holds ${formatSats(chosen.sats)}. Speeding it up from there costs about ${formatSats(
            advice.cpfp_child_fee_sats
          )} in fees, ${chosen.sats <= advice.cpfp_child_fee_sats ? "more than it holds" : "which leaves too little to send on"}`
        : "The other way is to spend the change this payment sent back to you, and it sent none";
    // a hardware wallet's own app may replace it without the signal (Trezor Suite), where Sparrow can't
    const fullRbf = route.device && bumpsIt(route.device, tx) ? route.device : null;
    body = (
      <Box data-wizard-step="steps" data-wizard-path={tooSmall ? "change-too-small" : "no-change"}>
        <Stack gap="sm">
          <Question>{tooSmall ? "Your change is too small to speed it up with" : "There's no change to speed it up with"}</Question>
          <Text size="sm" c="dimmed">
            {noBump} {noChange}
            {fullRbf ? "." : ", so you can't speed it up yourself."}
          </Text>
          {fullRbf && (
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                {fullRbf.name} replaces a payment even when it doesn't signal RBF.
              </Text>
              <Button size="xs" variant="outline" color="orange" onClick={() => go(withWallet(fullRbf.id))}>
                Show me how in {fullRbf.name}
              </Button>
            </Group>
          )}
          <Text size="sm" c="dimmed">
            {fullRbf ? "Or the" : "The"} recipient can speed it up from the bitcoin they're receiving, if their wallet can: send them
            the link to this page.
          </Text>
          <Group gap="xs">
            <CopyIt value={shareUrl} label="Copy the link to this page" size="sm" />
          </Group>
          <Fallbacks title="Or" />
        </Stack>
      </Box>
    );
  } else if (wallet && wallet.kind === "custodial") {
    body = (
      <Box data-wizard-step="steps" data-wizard-path="custodial">
        {role === "sent" ? (
          askExchangeBlock(
            <>
              {wallet.name} sent this payment and holds the coins, so only {wallet.name} can change its fee.{" "}
              {wallet.support && (
                <Anchor href={wallet.support} target="_blank" rel="noopener nofollow" c="orange.4" size="sm">
                  {wallet.name} support
                </Anchor>
              )}
            </>,
            wallet.name
          )
        ) : role === "exchange" ? (
          askExchangeBlock(
            `${wallet.name} holds your bitcoin for you, so it can't speed up a payment coming in. Only the exchange that sent it can, so contact its support and give them the transaction ID.`
          )
        ) : (
          askSenderBlock(
            `${wallet.name} holds your bitcoin for you, so it can't spend an incoming payment early to speed it up. The sender can: send them the link to this page.`
          )
        )}
      </Box>
    );
  } else if (wallet && !route) {
    body = (
      <Box data-wizard-step="steps" data-wizard-path="cannot">
        {role === "sent" ? (
          <Stack gap="sm">
            <Question>{wallet.name} can't speed this one up</Question>
            <Text size="sm" c="dimmed">
              {wallet.send.method === "rbf" && !tx.rbf_signaled
                ? `This transaction doesn't signal RBF, and ${wallet.name} only bumps the fee of transactions that do.`
                : `${wallet.name} has no way to raise the fee of a payment you sent.`}{" "}
              {wallet.send.note}
            </Text>
            <Fallbacks title="What you can do instead" />
          </Stack>
        ) : role === "exchange" ? (
          askExchangeBlock(
            `${wallet.name} can't speed up a payment you're receiving. ${wallet.receive.note ?? ""} Only the exchange that sent it can, so contact its support and give them the transaction ID.`
          )
        ) : (
          askSenderBlock(
            `${wallet.name} can't speed up a payment you're receiving. ${wallet.receive.note ?? ""} The sender can: send them the link to this page.`
          )
        )}
      </Box>
    );
  } else if (route && wallet) {
    body = (
      <StepsView
        tx={tx}
        advice={advice}
        data={data}
        route={route}
        role={role}
        usd={usd}
        fallbacks={<Fallbacks sparrowFor={route.wallet.id !== "sparrow" && wallet.fallback === "sparrow" ? wallet : null} />}
        onDone={() => go({ ...state, step: "new-txid" })}
        output={chosen}
        outputIndex={typeof state.output === "number" ? state.output : null}
      />
    );
  }

  return (
    <Paper p="md" radius="md" style={PANEL_STYLE} ref={rootRef} id="speed-up" data-wizard>
      <WizardStyles />
      <Stack gap="md">
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <IconRocket size={18} color="var(--mantine-color-orange-5)" />
            <Text c="white" fw={700}>
              Speed it up
            </Text>
          </Group>
          {(role || unsure) && (
            <UnstyledButton onClick={back} c="orange.4" style={{ fontSize: 14 }}>
              <Group gap={4} wrap="nowrap">
                <IconArrowLeft size={14} />
                Back
              </Group>
            </UnstyledButton>
          )}
        </Group>

        {answers.length > 0 && (
          <Group gap={6} data-wizard-answers>
            {answers.map((answer, i) => (
              <React.Fragment key={answer.label}>
                {i > 0 && (
                  <Text span c="dimmed" size="sm">
                    ·
                  </Text>
                )}
                <UnstyledButton onClick={() => go(answer.to)} title="Change this answer" style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
                  <Group gap={4} wrap="nowrap">
                    {answer.label}
                    <IconPencil size={12} color="var(--mantine-color-orange-4)" />
                  </Group>
                </UnstyledButton>
              </React.Fragment>
            ))}
          </Group>
        )}

        <Box key={`${view}:${unsure}:${state.wallet}:${state.via}:${state.output}`} className="rz-wizard-step">
          {body}
        </Box>
      </Stack>
    </Paper>
  );
};

/** Which output is theirs: the receiver's, or the sender's change. That's the coin CPFP spends. */
const OutputStep: React.FC<{
  outputs: { output: TxOutput; index: number }[];
  extra: number;
  kind: "receiving" | "exchange" | "change";
  /** why a sender's way is their change */
  notice: string | null;
  onPick: (index: number | "none") => void;
}> = ({ outputs, extra, kind, notice, onPick }) => {
  const [query, setQuery] = useState("");
  const searchable = outputs.length > 6;
  const q = query.trim().toLowerCase();
  const shown = q ? outputs.filter(({ output }) => output.address.toLowerCase().includes(q)) : outputs;
  const limit = 12;

  return (
    <Stack gap="sm" data-wizard-step="output" data-wizard-view={kind === "change" ? "change" : undefined}>
      <Question>{kind === "change" ? "Which of these addresses is your change?" : "Which of these addresses is yours?"}</Question>
      {notice && (
        <Text size="sm" c="yellow.4">
          {notice}
        </Text>
      )}
      <Text size="sm" c="dimmed">
        {kind === "change"
          ? "Most payments send part of the coins back to your own wallet: that's your change. Find which of these addresses is in your wallet (in its list of addresses, or in this payment's details) and tap it."
          : kind === "exchange"
            ? "Find the address you gave the exchange (your wallet's receiving address) and tap it."
            : "Find your receiving address in your wallet and tap the one that matches."}
      </Text>
      {searchable && (
        <TextInput
          aria-label="Find your address"
          placeholder="Type the first few characters of your address"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          styles={{ input: { fontFamily: "monospace", backgroundColor: "rgba(0, 0, 0, 0.3)", border: "1px solid rgba(255, 255, 255, 0.15)", color: "white" } }}
        />
      )}
      <Stack gap={8}>
        {shown.slice(0, limit).map(({ output, index }) => (
          <Choice key={index} onClick={() => onPick(index)}>
            <Group justify="space-between" gap="xs" wrap="nowrap" align="flex-start">
              <Text span inherit style={{ ...MONO, minWidth: 0, overflowWrap: "anywhere", fontSize: 14 }}>
                {output.address}
              </Text>
              <Text span inherit style={{ ...MONO, whiteSpace: "nowrap", fontSize: 14 }}>
                {formatBtc(output.sats)}
              </Text>
            </Group>
          </Choice>
        ))}
        {shown.length > limit && (
          <Text size="xs" c="dimmed">
            {shown.length - limit} more: type the start of your address to find it.
          </Text>
        )}
        {q && shown.length === 0 && (
          <Text size="xs" c="dimmed">
            No address here starts like that.
          </Text>
        )}
        {extra > 0 && (
          <Text size="xs" c="dimmed">
            This transaction has {extra} more outputs we can't list.
          </Text>
        )}
        <Choice onClick={() => onPick("none")}>
          {kind === "exchange" ? "None of these: it went to another exchange or app" : "None of these is mine"}
        </Choice>
      </Stack>
    </Stack>
  );
};

/** The numbered steps for one wallet and one role, with the number to enter where it's asked for. */
const StepsView: React.FC<{
  tx: TxLookupResult;
  advice: Advice;
  data: FeeBumpData;
  route: Route;
  role: Role;
  usd: (sats: number) => string;
  fallbacks: React.ReactNode;
  onDone: () => void;
  output: TxOutput | null;
  outputIndex: number | null;
}> = ({ tx, advice, data, route, role, usd, fallbacks, onDone, output, outputIndex }) => {
  const { method, wallet, device, path } = route;
  const numbers = numbersFor(path.enter, method, advice, !!path.whole);
  const values: Record<string, string> = {
    txid: tx.txid,
    rate: numbers[0]?.value ?? String(ceilTo(method === "rbf" ? advice.rbf_fee_rate : advice.target_fee_rate, 1)),
    device: device ? deviceName(device) : "hardware wallet",
  };
  if (output && outputIndex !== null) {
    values.vout = String(outputIndex);
    values.amount = (output.sats / 100_000_000).toFixed(8);
  }
  const setup = device ? data.sparrow_setup.steps : [];
  const steps = [...setup, ...(path.steps ?? [])].map((step) => fillStep(step, values));
  const enterAt = numbers.length > 0 ? (path.enter_at ? setup.length + path.enter_at : steps.length) : null;
  const intro = method === "rbf" ? RBF_INTRO : role === "sent" ? CPFP_SEND_INTRO : CPFP_RECEIVE_INTRO;
  // the wallet may pick the fee itself (Wasabi, Exodus, Bitcoin Core's button): then this is a yardstick
  const picksItself = numbers.length === 0;
  const cost =
    method === "rbf"
      ? picksItself
        ? `${wallet.name} works out the new fee itself. For the next block it needs a total fee of about ${formatSats(
            advice.rbf_fee_sats
          )}${usd(advice.rbf_fee_sats)}, ${formatSats(advice.rbf_extra_sats)} more than it pays now.`
        : `That makes the total fee about ${formatSats(advice.rbf_fee_sats)}${usd(advice.rbf_fee_sats)}, ${formatSats(
            advice.rbf_extra_sats
          )} more than it pays now.`
      : picksItself
        ? `${wallet.name} works out the fee itself. For the next block the new transaction needs a fee of about ${formatSats(
            advice.cpfp_child_fee_sats
          )}${usd(advice.cpfp_child_fee_sats)}, which pays for both.`
        : `The new transaction's fee comes to about ${formatSats(advice.cpfp_child_fee_sats)}${usd(
            advice.cpfp_child_fee_sats
          )}, and it pays for both.`;
  const signalNote =
    method === "rbf" && !tx.rbf_signaled && path.signal_required === undefined
      ? `This transaction doesn't signal RBF. If ${wallet.name} doesn't offer a fee bump for it, see below.`
      : null;

  return (
    <Stack gap="sm" data-wizard-step="steps" data-wizard-method={method}>
      <Question>
        {device ? `Speed it up in Sparrow, with your ${deviceName(device)}` : `Speed it up in ${wallet.name}`}
      </Question>
      {route.notice && (
        <Text size="sm" c="yellow.4">
          {route.notice}
        </Text>
      )}
      <Text size="sm" c="dimmed">
        {intro}
      </Text>
      {device && (
        <Text size="sm" c="dimmed">
          Sparrow only talks to your {deviceName(device)}: it never needs your recovery words.{" "}
          {device.device_note && <Labelled text={device.device_note} />}
        </Text>
      )}
      <Stack gap={10} component="ol" style={{ margin: 0, paddingLeft: 22 }}>
        {steps.map((step, i) => (
          <Box component="li" key={i} c="rgba(255,255,255,0.85)" style={{ fontSize: 15, lineHeight: 1.5 }}>
            <Labelled text={step} />
            {enterAt === i + 1 && (
              <Stack gap={6} mt={8}>
                {numbers.map((number) => (
                  <NumberBox key={number.label} number={number} />
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
      <Text size="sm" c="dimmed">
        {cost}{picksItself ? "" : " Your wallet may show a slightly different number for the same fee rate: that's fine."}
      </Text>
      {path.note && (
        <Text size="sm" c="dimmed">
          <Labelled text={path.note} />
        </Text>
      )}
      {signalNote && (
        <Text size="sm" c="dimmed">
          {signalNote}
        </Text>
      )}
      {path.success && (
        <Text size="sm" c="teal.4">
          When it worked: <Labelled text={path.success} />
        </Text>
      )}
      {method === "rbf" ? (
        <Group>
          <Button color="orange" onClick={onDone}>
            Done: check the new transaction ID
          </Button>
        </Group>
      ) : (
        <Text size="sm" c="dimmed">
          This transaction keeps its ID. The page keeps checking it on every new block: once your new transaction is out, the
          status at the top should say it will confirm in the next block.
        </Text>
      )}
      {fallbacks}
    </Stack>
  );
};

export default SpeedUpWizard;
